import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { ArrowLeft, Plus, Trash2, EyeClosed, Clipboard } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

// Long press duration in milliseconds
const LONG_PRESS_DURATION = 400;

interface ColorPickerProps {
  onComplete: (colors: string[]) => void;
  onCancel: (buttonRect?: DOMRect) => void;
  initialColors?: string[];
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
}

// Sample the actual color from the saturation/lightness picker element
function sampleColorFromGradient(hue: number, sat: number, x: number, y: number): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return hslToHex(hue, sat, (1 - y) * 100);
  
  // Use a small canvas size for performance
  const size = 200;
  canvas.width = size;
  canvas.height = size;
    
    // Recreate the same gradient layers (must match innerCircleBackground CSS)
    // Layer 1: base linear gradient
    const baseGrad = ctx.createLinearGradient(0, 0, size, size);
    baseGrad.addColorStop(0, '#fff');
    baseGrad.addColorStop(0.5, `hsl(${hue}, ${sat}%, 50%)`);
    baseGrad.addColorStop(1, '#000');
    ctx.fillStyle = baseGrad;
    ctx.fillRect(0, 0, size, size);
    
    // Layer 2: white corner
    const whiteGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 0.6);
    whiteGrad.addColorStop(0, 'rgba(255,255,255,1)');
    whiteGrad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = whiteGrad;
    ctx.fillRect(0, 0, size, size);
    
    // Layer 3: hue corner
    const hueGrad = ctx.createRadialGradient(size, 0, 0, size, 0, size * 0.6);
    hueGrad.addColorStop(0, `hsla(${hue}, ${sat}%, 50%, 1)`);
    hueGrad.addColorStop(1, `hsla(${hue}, ${sat}%, 50%, 0)`);
    ctx.fillStyle = hueGrad;
    ctx.fillRect(0, 0, size, size);
    
    // Layer 4: dark bottom-left corner
    const darkGrad1 = ctx.createRadialGradient(0, size, 0, 0, size, size * 0.5);
    darkGrad1.addColorStop(0, 'rgba(0,0,0,0.5)');
    darkGrad1.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = darkGrad1;
    ctx.fillRect(0, 0, size, size);
    
    // Layer 5: dark bottom-right corner (strongest)
    const darkGrad2 = ctx.createRadialGradient(size, size, 0, size, size, size * 0.7);
    darkGrad2.addColorStop(0, 'rgba(0,0,0,0.9)');
    darkGrad2.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = darkGrad2;
    ctx.fillRect(0, 0, size, size);
    
    // Sample the pixel at the normalized position
    const sampleX = Math.floor(x * size);
    const sampleY = Math.floor(y * size);
    const pixel = ctx.getImageData(
      Math.max(0, Math.min(size - 1, sampleX)),
      Math.max(0, Math.min(size - 1, sampleY)),
      1, 1
    ).data;
    
  return '#' + [pixel[0], pixel[1], pixel[2]]
    .map(v => v.toString(16).padStart(2, '0'))
    .join('').toUpperCase();
}

// Parse hex color to HSL (approximate)
function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function ColorPicker({ onComplete, onCancel, initialColors }: ColorPickerProps) {
  const [selectedColors, setSelectedColors] = useState<string[]>(initialColors || []);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [hue, setHue] = useState(15);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(60);
  const [innerX, setInnerX] = useState(70); // Inner circle dot X position (independent of saturation bar)
  const [sampledColor, setSampledColor] = useState<string | null>(null);
  const [flashColor, setFlashColor] = useState<string | null>(null);
  const [isBlinking, setIsBlinking] = useState(false);
  const [hexInput, setHexInput] = useState('');
  const [isHexFocused, setIsHexFocused] = useState(false);
  const [grayscaleMode, setGrayscaleMode] = useState(false);
  const [grayscaleParticles, setGrayscaleParticles] = useState<Array<{
    id: number;
    emoji: string;
    angle: number;
    distance: number;
    size: number;
    delay: number;
    rotation: number;
  }>>([]);
  const [grayscaleAnimKey, setGrayscaleAnimKey] = useState(0);
  // State to track if hex input is actively overriding the picker
  const [hexOverride, setHexOverride] = useState<string | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const satLightRef = useRef<HTMLDivElement>(null);
  const satBarRef = useRef<HTMLDivElement>(null);
  const hexInputRef = useRef<HTMLInputElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const isDragging = useRef<'satlight' | 'hue' | 'satbar' | null>(null);
  const satBarEndpointFired = useRef<'left' | 'right' | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Drag reorder state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const colorCirclesRef = useRef<HTMLDivElement>(null);
  
  const { toast } = useToast();

  // Track window width for responsive spread angle
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fallbackColor = hslToHex(hue, saturation, lightness);
  // Hex override takes priority for exact color matching
  const currentColor = hexOverride || sampledColor || fallbackColor;

  // Clear flash after animation
  useEffect(() => {
    if (flashColor) {
      const timer = setTimeout(() => setFlashColor(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [flashColor]);

  // Clear blink after animation
  useEffect(() => {
    if (isBlinking) {
      const timer = setTimeout(() => setIsBlinking(false), 600);
      return () => clearTimeout(timer);
    }
  }, [isBlinking]);

  // Clear grayscale particles after animation
  useEffect(() => {
    if (grayscaleParticles.length > 0) {
      const timer = setTimeout(() => setGrayscaleParticles([]), 1200);
      return () => clearTimeout(timer);
    }
  }, [grayscaleParticles.length, grayscaleAnimKey]);

  // Resample color when hue/innerX/lightness/saturation changes
  useEffect(() => {
    const x = innerX / 100;
    const y = (100 - lightness) / 100;
    // Sample directly from canvas that matches the CSS gradient (includes saturation)
    const color = sampleColorFromGradient(hue, saturation, x, y);
    setSampledColor(color);
  }, [hue, innerX, lightness, saturation]);

  // Sync hex input with current color when not focused
  useEffect(() => {
    if (!isHexFocused) {
      setHexInput(currentColor);
    }
  }, [currentColor, isHexFocused]);

  // Ring dimensions
  const ringThickness = 28;
  const ringSize = 280; // outer diameter
  const innerSize = ringSize - ringThickness * 2;

  const handleSatLightMove = useCallback((clientX: number, clientY: number, _currentHue: number) => {
    if (!satLightRef.current) return;
    const rect = satLightRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    setInnerX(Math.round(x * 100));
    setLightness(Math.round(100 - y * 100));
    
    // Clear hex override when user interacts with canvas
    setHexOverride(null);
  }, []);

  const triggerGrayscaleToggle = useCallback((toGrayscale: boolean) => {
    setGrayscaleMode(toGrayscale);
    setHexOverride(null);
    setSampledColor(null);
    
    // Burst particles on toggle
    const emojis = toGrayscale
      ? ['●', '◐', '◑', '○', '◒', '◓', '◔', '◕']
      : ['🌈', '✨', '💎', '🔮', '💜', '💛', '💚', '🧡'];
    const particles = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
      angle: (360 / 12) * i + (Math.random() * 20 - 10),
      distance: 40 + Math.random() * 60,
      size: 8 + Math.random() * 10,
      delay: Math.random() * 0.1,
      rotation: Math.random() * 360 - 180,
    }));
    setGrayscaleParticles(particles);
    setGrayscaleAnimKey(k => k + 1);
    
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
    
    // Toast
    const grayMessages = [
      'The world in shades of maybe.',
      'Color has left the building.',
      'Fifty shades of yourself.',
      'Monochrome mood activated.',
      'How dramatic of you.',
      'Everything is a little quieter now.',
    ];
    const colorMessages = [
      'Welcome back to the spectrum.',
      'Color remembered you.',
      'The rainbow missed you.',
      'Feeling chromatic again.',
      'Gray was getting old anyway.',
      'Eyes wide open.',
    ];
    const messages = toGrayscale ? grayMessages : colorMessages;
    toast({ description: messages[Math.floor(Math.random() * messages.length)] });
  }, [toast]);

  const handleSatBarMove = useCallback((clientX: number) => {
    if (!satBarRef.current) return;
    const rect = satBarRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const value = Math.round(x * 100);
    setSaturation(value);
    setHexOverride(null);

    // Trigger grayscale/color toggle at endpoints
    if (value === 0 && !grayscaleMode && satBarEndpointFired.current !== 'left') {
      satBarEndpointFired.current = 'left';
      triggerGrayscaleToggle(true);
    } else if (value === 100 && grayscaleMode && satBarEndpointFired.current !== 'right') {
      satBarEndpointFired.current = 'right';
      triggerGrayscaleToggle(false);
    } else if (value > 0 && value < 100) {
      satBarEndpointFired.current = null;
    }
  }, [grayscaleMode, triggerGrayscaleToggle]);

  const handleHueMove = useCallback((clientX: number, clientY: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Calculate angle from center
    const dx = clientX - centerX;
    const dy = clientY - centerY;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);
    
    // Convert to 0-360, starting from top (12 o'clock position)
    angle = (angle + 90 + 360) % 360;
    
    setHue(Math.round(angle));
    // Clear hex override; sampledColor will be recalculated by the effect
    setHexOverride(null);
  }, []);

  const handlePointerDown = (e: React.PointerEvent, type: 'satlight' | 'hue' | 'satbar') => {
    // At zero saturation, don't allow hue ring interaction
    if (type === 'hue' && saturation === 0) return;
    isDragging.current = type;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    if (type === 'satlight') {
      handleSatLightMove(e.clientX, e.clientY, hue);
    } else if (type === 'satbar') {
      handleSatBarMove(e.clientX);
    } else {
      handleHueMove(e.clientX, e.clientY);
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (isDragging.current === 'satlight') {
      handleSatLightMove(e.clientX, e.clientY, hue);
    } else if (isDragging.current === 'satbar') {
      handleSatBarMove(e.clientX);
    } else if (isDragging.current === 'hue') {
      handleHueMove(e.clientX, e.clientY);
    }
  };

  const handlePointerUp = () => {
    if (isDragging.current === 'satbar') {
      satBarEndpointFired.current = null;
    }
    isDragging.current = null;
  };

  const handleAddColor = () => {
    if (selectedColors.length < 6 && !selectedColors.includes(currentColor)) {
      if (navigator.vibrate) navigator.vibrate(15);
      const newColors = [...selectedColors, currentColor];
      setSelectedColors(newColors);
      setSelectedIndex(newColors.length - 1);
      setFlashColor(currentColor);
      setIsBlinking(true);
    }
  };

  const handleSelectColor = (index: number) => {
    setSelectedIndex(selectedIndex === index ? null : index);
  };

  const handleClearSelected = () => {
    if (selectedIndex !== null) {
      const newColors = selectedColors.filter((_, i) => i !== selectedIndex);
      setSelectedColors(newColors);
      // Select the previous circle, or null if we deleted the first one
      if (selectedIndex > 0) {
        setSelectedIndex(selectedIndex - 1);
      } else if (newColors.length > 0) {
        setSelectedIndex(0);
      } else {
        setSelectedIndex(null);
      }
    }
  };

  const handleComplete = () => {
    if (selectedColors.length >= 3) {
      onComplete(selectedColors);
    }
  };

  const handleHexInputChange = (value: string) => {
    // Allow typing with or without #
    let cleaned = value.toUpperCase().replace(/[^0-9A-F#]/g, '');
    if (!cleaned.startsWith('#')) {
      cleaned = '#' + cleaned.replace(/#/g, '');
    }
    // Limit to 7 characters (#RRGGBB)
    cleaned = cleaned.slice(0, 7);
    setHexInput(cleaned);
    
    // If we have a valid 6-digit hex, hard override the color
    if (/^#[0-9A-F]{6}$/i.test(cleaned)) {
      // Set the hex override - this takes priority over canvas sampling
      setHexOverride(cleaned);
      setSampledColor(cleaned);
      
      // Also update HSL for visual positioning (but the hex override ensures exact color)
      const hsl = hexToHsl(cleaned);
      if (hsl) {
        setHue(hsl.h);
        setSaturation(hsl.s);
        setLightness(hsl.l);
      }
    } else {
      // Clear override when hex is incomplete
      setHexOverride(null);
    }
  };

  const handlePasteHex = async () => {
    try {
      const text = await navigator.clipboard.readText();
      // Clean up the pasted text
      let cleaned = text.trim().toUpperCase().replace(/[^0-9A-F#]/g, '');
      if (!cleaned.startsWith('#') && /^[0-9A-F]{6}$/i.test(cleaned)) {
        cleaned = '#' + cleaned;
      }
      if (/^#[0-9A-F]{6}$/i.test(cleaned)) {
        handleHexInputChange(cleaned);
        toast({ description: 'Color pasted!' });
      } else {
        toast({ description: 'Invalid hex color', variant: 'destructive' });
      }
    } catch {
      toast({ description: 'Could not read clipboard', variant: 'destructive' });
    }
  };



  const canComplete = selectedColors.length >= 3;
  const remaining = Math.max(0, 3 - selectedColors.length);

  // Calculate hue thumb position on the ring
  // Angle in radians, offset by -90deg to start from top
  const hueAngleRad = ((hue - 90) * Math.PI) / 180;
  const thumbRadius = (ringSize - ringThickness) / 2;
  const thumbX = Math.cos(hueAngleRad) * thumbRadius;
  const thumbY = Math.sin(hueAngleRad) * thumbRadius;

  // Calculate positions for color circles around top of wheel
  const colorCircleRadius = ringSize / 2 + 48; // Distance from center
  const circleButtonSize = 56; // w-14 = 56px
  
  // Calculate max spread angle based on available width
  // Circles are centered on the screen, so we have windowWidth/2 on each side
  // Need to keep circles within viewport: center + x + circleButtonSize/2 < windowWidth/2
  // So max x = windowWidth/2 - circleButtonSize/2 - some padding
  const maxX = windowWidth / 2 - circleButtonSize / 2 - 16; // 16px padding from edge
  // x = cos(angle) * radius, so maxAngle where cos(angle) * radius = maxX
  // For angles near -90° (top), the edge angles are around -90 ± spread/2
  // cos(-90 + spread/2) ≈ sin(spread/2) for small angles
  const maxSin = Math.min(1, maxX / colorCircleRadius);
  const maxHalfSpread = Math.asin(maxSin) * (180 / Math.PI);
  const maxSpreadAngle = Math.min(150, maxHalfSpread * 2);
  
  const getColorPosition = useCallback((index: number, total: number) => {
    // Spread angle grows gradually as more circles are added
    const spreadAngle = total === 1 ? 0 : ((total - 1) / 5) * maxSpreadAngle;
    const startAngle = -90 - spreadAngle / 2; // Center around top
    const angleStep = total > 1 ? spreadAngle / (total - 1) : 0;
    const angle = total === 1 ? -90 : startAngle + (index * angleStep);
    const rad = (angle * Math.PI) / 180;
    return {
      x: Math.cos(rad) * colorCircleRadius,
      y: Math.sin(rad) * colorCircleRadius,
    };
  }, [maxSpreadAngle, colorCircleRadius]);

  // Cleanup long press timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  // Track if long press was triggered to prevent click
  const longPressTriggered = useRef(false);

  // Handle long press start for drag reorder
  const handleColorPointerDown = useCallback((e: React.PointerEvent, index: number) => {
    // Only handle filled color circles
    if (index >= selectedColors.length) return;
    
    const startX = e.clientX;
    const startY = e.clientY;
    dragStartPos.current = { x: startX, y: startY };
    longPressTriggered.current = false;
    
    // Capture pointer to track moves outside element
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    
    // Start long press timer
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      // Start dragging
      setDragIndex(index);
      setDragPosition({ x: startX, y: startY });
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, LONG_PRESS_DURATION);
  }, [selectedColors.length]);

  // Handle pointer move during drag
  const handleColorPointerMove = useCallback((e: React.PointerEvent) => {
    // If we're in drag mode, update position
    if (dragIndex !== null) {
      setDragPosition({ x: e.clientX, y: e.clientY });
      return;
    }
    
    // Cancel long press if moved too far before it triggered
    if (dragStartPos.current && longPressTimer.current) {
      const dx = e.clientX - dragStartPos.current.x;
      const dy = e.clientY - dragStartPos.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
  }, [dragIndex]);

  // Find which index the dragged item should be dropped at
  const getDropIndex = useCallback((dragX: number, dragY: number): number => {
    if (dragIndex === null) return -1;
    
    const colorCount = selectedColors.length;
    let closestIndex = dragIndex;
    let closestDistance = Infinity;
    
    for (let i = 0; i < colorCount; i++) {
      if (i === dragIndex) continue;
      
      const pos = getColorPosition(i, Math.min(colorCount + 1, 6));
      // Get center of wheel
      if (!containerRef.current) continue;
      const wheelRect = containerRef.current.getBoundingClientRect();
      const centerX = wheelRect.left + wheelRect.width / 2;
      const centerY = wheelRect.top + wheelRect.height / 2;
      
      const circleX = centerX + pos.x;
      const circleY = centerY + pos.y;
      
      const distance = Math.sqrt(
        Math.pow(dragX - circleX, 2) + Math.pow(dragY - circleY, 2)
      );
      
      if (distance < closestDistance && distance < 80) {
        closestDistance = distance;
        closestIndex = i;
      }
    }
    
    return closestIndex;
  }, [dragIndex, selectedColors.length, getColorPosition]);

  // Calculate drop index during drag
  const dropIndex = useMemo(() => {
    if (dragIndex === null || !dragPosition) return null;
    return getDropIndex(dragPosition.x, dragPosition.y);
  }, [dragIndex, dragPosition, getDropIndex]);

  // Handle actual reordering when pointer up with valid drop
  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && dropIndex !== null && dragIndex !== dropIndex) {
      const newColors = [...selectedColors];
      const [removed] = newColors.splice(dragIndex, 1);
      newColors.splice(dropIndex, 0, removed);
      setSelectedColors(newColors);
      
      // Update selected index to follow the dragged item
      if (selectedIndex === dragIndex) {
        setSelectedIndex(dropIndex);
      } else if (selectedIndex !== null) {
        // Adjust selected index if it was affected by the move
        if (dragIndex < selectedIndex && dropIndex >= selectedIndex) {
          setSelectedIndex(selectedIndex - 1);
        } else if (dragIndex > selectedIndex && dropIndex <= selectedIndex) {
          setSelectedIndex(selectedIndex + 1);
        }
      }
    }
    
    setDragIndex(null);
    setDragPosition(null);
  }, [dragIndex, dropIndex, selectedColors, selectedIndex]);

  // Modified pointer up to handle reordering
  const handleColorPointerUpWithReorder = useCallback(() => {
    // Clear long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    dragStartPos.current = null;
    
    // Handle reorder if drag was in progress
    if (dragIndex !== null) {
      handleDragEnd();
    }
  }, [dragIndex, handleDragEnd]);

  // Inner circle background - saturation drives the color intensity
  const innerCircleBackground = `
    radial-gradient(circle at 100% 100%, rgba(0,0,0,0.9) 0%, transparent 70%),
    radial-gradient(circle at 0% 100%, rgba(0,0,0,0.5) 0%, transparent 50%),
    radial-gradient(circle at 100% 0%, hsl(${hue}, ${saturation}%, 50%) 0%, transparent 60%),
    radial-gradient(circle at 0% 0%, #fff 0%, transparent 60%),
    linear-gradient(135deg, #fff 0%, hsl(${hue}, ${saturation}%, 50%) 50%, #000 100%)
  `;

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-x-hidden">
      {/* Background layer - saturation drives color intensity */}
      <div
        className="absolute inset-0 transition-[background] duration-300"
        style={{
          background: `linear-gradient(to bottom right, hsl(350, ${saturation * 0.4}%, 85%), hsl(40, ${saturation * 0.3}%, 90%), hsl(150, ${saturation * 0.3}%, 90%), hsl(200, ${saturation * 0.4}%, 85%))`,
        }}
      />

      {/* Wave overlay - behind UI (always mounted to avoid repaint flicker) */}
      <div 
        className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden"
        style={{ zIndex: 1, opacity: flashColor ? 1 : 0 }}
      >
        {flashColor && (
          <div 
            className="aspect-square w-[100vmin] rounded-full animate-color-wave"
            style={{
              background: `radial-gradient(circle, ${flashColor}aa 0%, ${flashColor}66 30%, ${flashColor}33 50%, ${flashColor}11 70%, transparent 85%)`,
            }}
          />
        )}
      </div>

      {/* Header */}
      <div className="max-w-xl mx-auto w-full px-5 pb-4 safe-top-mobile relative z-10" style={{ '--safe-top-min': '1.5rem' } as React.CSSProperties}>
        <button
          ref={backButtonRef}
          onClick={() => {
            const rect = backButtonRef.current?.getBoundingClientRect();
            onCancel(rect);
          }}
          className="p-2 -ml-2 rounded-full text-foreground/60 hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="mt-1">
          <h2 className="text-lg font-semibold">Pick colors</h2>
          <p className="text-xs text-muted-foreground">
            {remaining > 0 ? `${remaining} more needed` : 'Ready!'}
          </p>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-6 relative z-10">
        
        {/* Circular color picker */}
        <div
          ref={containerRef}
          className="relative mt-8 mb-2"
          style={{ width: ringSize, height: ringSize }}
        >
          {/* Hue ring */}
          <div
            className={`absolute inset-0 rounded-full touch-none ${saturation === 0 ? 'pointer-events-none' : 'cursor-pointer'}`}
            style={{
              background: `conic-gradient(from 0deg, 
                hsl(0, ${saturation}%, 50%), 
                hsl(60, ${saturation}%, 50%), 
                hsl(120, ${saturation}%, 50%), 
                hsl(180, ${saturation}%, 50%), 
                hsl(240, ${saturation}%, 50%), 
                hsl(300, ${saturation}%, 50%), 
                hsl(360, ${saturation}%, 50%)
              )`,
              willChange: 'background',
            }}
            onPointerDown={(e) => handlePointerDown(e, 'hue')}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
          
          {/* Inner circle mask (creates the ring effect) */}
          <div
            className="absolute rounded-full transition-[background] duration-300"
            style={{
              width: innerSize,
              height: innerSize,
              top: ringThickness,
              left: ringThickness,
              background: `linear-gradient(to bottom right, hsl(350, ${saturation * 0.4}%, 85%), hsl(40, ${saturation * 0.3}%, 90%), hsl(150, ${saturation * 0.3}%, 90%), hsl(200, ${saturation * 0.4}%, 85%))`,
            }}
          />

          {/* Saturation/Lightness picker inside the ring - The Eye */}
          <div
            ref={satLightRef}
            className="absolute rounded-full cursor-crosshair touch-none overflow-hidden shadow-inner"
            style={{
              width: innerSize - 24,
              height: innerSize - 24,
              top: ringThickness + 12,
              left: ringThickness + 12,
              background: innerCircleBackground,
            }}
            onPointerDown={(e) => handlePointerDown(e, 'satlight')}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          >
            {/* Sat/Light picker dot */}
            <div
              className="absolute w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white shadow-lg pointer-events-none"
              style={{
                left: `${innerX}%`,
                top: `${100 - lightness}%`,
                backgroundColor: currentColor,
              }}
            />
            
            {/* Eyelid - circular shape that moves down and up */}
            {isBlinking && (
              <div className="absolute inset-0 rounded-full pointer-events-none overflow-hidden">
                <div
                  className="absolute left-0 right-0 animate-eyelid-blink"
                  style={{
                    top: 0,
                    width: '100%',
                    height: '100%',
                  }}
                >
                  {/* The circular eyelid */}
                  <div
                    className="absolute rounded-full"
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: currentColor,
                      bottom: 0,
                      left: 0,
                    }}
                  />
                  {/* EyeClosed icon on the eyelid - clipped inside circle */}
                  <EyeClosed 
                    className="absolute w-full h-auto"
                    style={{ 
                      color: hslToHex(hue, saturation, Math.max(0, lightness - 30)),
                      top: '100%',
                      transform: 'translateY(-60%)',
                    }}
                    strokeWidth={1.5}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Hue thumb on the ring - fades with saturation */}
          <div
            className="absolute w-8 h-8 rounded-full border-4 border-white shadow-xl pointer-events-none transition-opacity duration-200"
            style={{
              left: `calc(50% + ${thumbX}px - 16px)`,
              top: `calc(50% + ${thumbY}px - 16px)`,
              backgroundColor: `hsl(${hue}, ${saturation}%, 50%)`,
              opacity: saturation > 0 ? 1 : 0,
            }}
          />

          {/* Selected colors - crowned around top of wheel */}
          {/* Show filled colors + one empty placeholder (unless at max) */}
          <div
            ref={colorCirclesRef}
            onPointerMove={handleColorPointerMove}
            onPointerUp={handleColorPointerUpWithReorder}
            onPointerCancel={handleColorPointerUpWithReorder}
            onPointerLeave={handleColorPointerUpWithReorder}
            className="absolute inset-0 touch-none"
            style={{ pointerEvents: dragIndex !== null ? 'auto' : 'none' }}
          >
            {(() => {
              const maxColors = 6;
              // Number of circles to display: colors + 1 placeholder (capped at max)
              const circleCount = Math.min(selectedColors.length + 1, maxColors);
              
              return Array.from({ length: circleCount }).map((_, i) => {
                const pos = getColorPosition(i, circleCount);
                const hasColor = i < selectedColors.length;
                const color = selectedColors[i];
                const isSelected = selectedIndex === i;
                const isDragged = dragIndex === i;
                const isDropTarget = dropIndex === i && dragIndex !== null && dragIndex !== i;
                
                // Calculate position for dragged item
                let displayPos = pos;
                if (isDragged && dragPosition && containerRef.current) {
                  const wheelRect = containerRef.current.getBoundingClientRect();
                  const centerX = wheelRect.left + wheelRect.width / 2;
                  const centerY = wheelRect.top + wheelRect.height / 2;
                  displayPos = {
                    x: dragPosition.x - centerX,
                    y: dragPosition.y - centerY,
                  };
                }
                
                return (
                  <div
                    key={i}
                    className={`absolute transition-all ${isDragged ? 'duration-0 z-50' : 'duration-300'} ${isDropTarget ? 'scale-75 opacity-50' : ''}`}
                    style={{
                      left: `calc(50% + ${displayPos.x}px - 28px)`,
                      top: `calc(50% + ${displayPos.y}px - 28px)`,
                    }}
                  >
                    {hasColor ? (
                      <button
                        onPointerDown={(e) => {
                          e.preventDefault();
                          handleColorPointerDown(e, i);
                        }}
                        onPointerMove={handleColorPointerMove}
                        onPointerUp={() => {
                          // Clear timer first
                          if (longPressTimer.current) {
                            clearTimeout(longPressTimer.current);
                            longPressTimer.current = null;
                          }
                          
                          // Handle click only if long press wasn't triggered and not dragging
                          if (!longPressTriggered.current && dragIndex === null) {
                            handleSelectColor(i);
                          }
                          
                          handleColorPointerUpWithReorder();
                          longPressTriggered.current = false;
                        }}
                        onPointerCancel={() => {
                          if (longPressTimer.current) {
                            clearTimeout(longPressTimer.current);
                            longPressTimer.current = null;
                          }
                          handleColorPointerUpWithReorder();
                          longPressTriggered.current = false;
                        }}
                        className={`relative w-14 h-14 rounded-full shadow-md transition-all touch-none select-none ${
                          isDragged 
                            ? 'scale-125 shadow-2xl cursor-grabbing' 
                            : 'hover:scale-110 active:scale-95 cursor-grab'
                        } ${
                          isSelected && !isDragged ? 'scale-110' : ''
                        }`}
                        style={{ 
                          backgroundColor: color,
                          pointerEvents: 'auto',
                        }}
                      >
                        {isSelected && !isDragged && (
                          <div
                            className="absolute inset-[-4px] rounded-full -z-10"
                            style={{ backgroundColor: color, opacity: 0.45 }}
                          />
                        )}
                      </button>
                    ) : (
                      <div 
                        className="w-14 h-14 rounded-full border-2 border-dashed transition-colors"
                        style={{
                          borderColor: i < 3 
                            ? `${currentColor}99` 
                            : `${currentColor}44`
                        }}
                      />
                    )}
                  </div>
                );
              });
            })()}
          </div>

          {/* Clear button - positioned at bottom left of wheel */}
          <button
            onClick={handleClearSelected}
            disabled={selectedIndex === null}
            className="absolute w-16 h-16 rounded-full shadow-lg bg-white/60 dark:bg-white/10 hover:bg-white/80 dark:hover:bg-white/20 hover:scale-110 active:scale-95 transition-all disabled:opacity-30 disabled:hover:scale-100 flex items-center justify-center"
            style={{ 
              left: -24,
              bottom: -40,
            }}
            title="Remove selected color"
          >
            <Trash2 className="h-6 w-6 text-foreground/70" />
          </button>

          {/* Add color button - positioned at bottom right of wheel */}
          <button
            onClick={handleAddColor}
            disabled={selectedColors.length >= 6 || selectedColors.includes(currentColor)}
            className="absolute w-16 h-16 rounded-full shadow-lg ring-4 ring-white/50 hover:scale-110 active:scale-95 transition-transform disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center"
            style={{ 
              backgroundColor: currentColor,
              right: -24,
              bottom: -40,
            }}
            title="Tap to add this color"
          >
            <Plus className="h-7 w-7 text-white drop-shadow-md" />
          </button>

          {/* EyeClosed icon - top layer, only visible after halfway point */}
          {isBlinking && (
            <div 
              className="absolute pointer-events-none animate-eyelid-blink-top"
              style={{
                top: ringThickness + 12,
                left: ringThickness + 12,
                width: innerSize - 24,
                height: innerSize - 24,
              }}
            >
              <EyeClosed 
                className="absolute w-full h-auto"
                style={{ 
                  color: hslToHex(hue, saturation, Math.max(0, lightness - 30)),
                  top: '100%',
                  transform: 'translateY(-60%)',
                }}
                strokeWidth={1.5}
              />
            </div>
          )}
        </div>

        {/* Saturation arc bar */}
        {(() => {
          const arcWidth = 160;
          const arcSag = 18;
          const barThickness = 8;
          const svgHeight = arcSag + barThickness;
          // Curve downward (convex) to follow the wheel's bottom edge
          const pathD = `M 0 0 Q ${arcWidth / 2} ${arcSag * 2} ${arcWidth} 0`;
          // Thumb: quadratic bezier B(t) = (1-t)²·P0 + 2(1-t)t·P1 + t²·P2
          const t = saturation / 100;
          const bx = 2 * (1 - t) * t * (arcWidth / 2) + t * t * arcWidth;
          const by = 2 * (1 - t) * t * (arcSag * 2);

          return (
            <div
              ref={satBarRef}
              className="relative cursor-pointer touch-none mb-3"
              style={{ width: arcWidth, height: svgHeight }}
              onPointerDown={(e) => handlePointerDown(e, 'satbar')}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            >
              <svg
                width={arcWidth}
                height={svgHeight}
                className="absolute inset-0"
                style={{ overflow: 'visible' }}
              >
                <defs>
                  <linearGradient id="sat-grad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={`hsl(${hue}, 0%, ${lightness}%)`} />
                    <stop offset="100%" stopColor={`hsl(${hue}, 100%, ${lightness}%)`} />
                  </linearGradient>
                </defs>
                <path
                  d={pathD}
                  fill="none"
                  stroke="url(#sat-grad)"
                  strokeWidth={barThickness}
                  strokeLinecap="round"
                />
              </svg>
              {/* Thumb */}
              <div
                className="absolute w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white shadow-md pointer-events-none"
                style={{
                  left: bx,
                  top: by,
                  backgroundColor: hslToHex(hue, saturation, lightness),
                }}
              />
              {/* Burst particles */}
              {grayscaleParticles.map((particle) => (
                <span
                  key={`${grayscaleAnimKey}-${particle.id}`}
                  className="absolute animate-snowflake-burst pointer-events-none"
                  style={{
                    left: bx,
                    top: by,
                    fontSize: `${particle.size}px`,
                    '--burst-x': `${Math.cos((particle.angle * Math.PI) / 180) * particle.distance}px`,
                    '--burst-y': `${Math.sin((particle.angle * Math.PI) / 180) * particle.distance}px`,
                    '--burst-rotation': `${particle.rotation}deg`,
                    animationDelay: `${particle.delay}s`,
                  } as React.CSSProperties}
                >
                  {particle.emoji}
                </span>
              ))}
            </div>
          );
        })()}

        {/* Hex input row */}
        <div className="flex items-center gap-2 mb-8">
          {/* Hex code input box */}
          <div className="flex items-center h-8 rounded-full overflow-hidden bg-white/50 dark:bg-white/10">
            <input
              ref={hexInputRef}
              type="text"
              value={hexInput}
              onChange={(e) => handleHexInputChange(e.target.value)}
              onFocus={() => setIsHexFocused(true)}
              onBlur={() => {
                setIsHexFocused(false);
              }}
              placeholder="#"
              className="w-[5.5rem] pl-3 bg-transparent text-foreground/70 text-sm font-medium uppercase outline-none tracking-wide leading-none placeholder:text-foreground/30 placeholder:font-bold"
              maxLength={7}
            />
            <button
              onClick={handlePasteHex}
              className="pl-1 pr-3 flex items-center justify-center text-foreground/50 hover:text-foreground/70 transition-colors"
              title="Paste hex color"
            >
              <Clipboard className="h-4 w-4" />
            </button>
          </div>


        </div>

        {/* Continue button */}
        <button
          onClick={handleComplete}
          disabled={!canComplete}
          className="w-full max-w-xs py-4 rounded-full text-white font-medium text-lg shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-40 disabled:hover:scale-100"
          style={{
            background: `linear-gradient(to right, hsl(350, ${saturation}%, 65%), hsl(40, ${saturation}%, 65%), hsl(150, ${saturation}%, 65%))`,
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
